// @flow strict
import * as React from 'react';

import {TitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {duotoneIconStyle} from 'src/utils/app-shell';


type ClassNames = $ReadOnly<{
  wrapper?: string,
  leftSlot?: string,
  rightSlot?: string,
}>;

type PageTitleContextType = {
  pageNameKey?: string,
  showBackButton?: boolean,
  onBack?: () => void,
  tabSlotComponent?: React.Node,
  rightSlotComponent?: React.Node,
  customPageNameComponent?: React.Node,
  pageTitleComponent?: React.Node,
  classNames?: ClassNames,
  hidePageTitle?: boolean,
};

type PageTitleContextActions = {
  setPageTitleProps: (props: $Shape<PageTitleContextType>) => void,
};

type PageTitleContextValue = {
  pageTitleState: PageTitleContextType,
  actions: PageTitleContextActions,
};

const initialState = {
  pageNameKey: '',
  showBackButton: false,
  onBack: () => {},
  tabSlotComponent: null,
  rightSlotComponent: null,
  customPageNameComponent: null,
  hidePageTitle: false,
};

export const PageTitleContext: React.Context<PageTitleContextValue> =
  React.createContext({
    pageTitleState: initialState,
    actions: {
      setPageTitleProps: () => {},
    },
  });

type PageTitleProviderProps = {
  children: React.Node,
};

export function PageTitleProvider({
  children,
}: PageTitleProviderProps): React.Node {
  const [pageTitleState, setPageTitleState] =
    React.useState<PageTitleContextType>(initialState);

  const actions: PageTitleContextActions = React.useMemo(
    () => ({
      setPageTitleProps: (props: $Shape<PageTitleContextType>) => {
        setPageTitleState(() => ({
          ...initialState,
          ...props,
        }));
      },
    }),
    [],
  );

  const contextValue = React.useMemo(() => {
    return {pageTitleState, actions};
  }, [pageTitleState, actions]);

  return (
    <PageTitleContext.Provider value={contextValue}>
      {children}
    </PageTitleContext.Provider>
  );
}

export function usePageTitle(): PageTitleContextValue {
  const context = React.useContext(PageTitleContext);
  if (!context) {
    throw new Error('usePageTitle must be used within a PageTitleProvider');
  }
  return context;
}

export const createPageTitleProps = ({
  title,
  onBack,
  showBackButton = true,
  pageNameKey = '',
  extraComponent = null,
  iconName = 'chart-column',
}: {
  title: string,
  onBack: () => void,
  showBackButton?: boolean,
  pageNameKey?: string,
  extraComponent?: React.Node | null,
  iconName?: string,
}): PageTitleContextType => ({
  showBackButton,
  onBack: showBackButton ? onBack : () => {},
  pageNameKey,
  customPageNameComponent: pageNameKey.length ? null : (
    <PageName>
      <TitleMedium>{title}</TitleMedium>
      {!!title.trim().length && (
        <Icon type="duotone" name={iconName} style={duotoneIconStyle} />
      )}
    </PageName>
  ),
  rightSlotComponent: extraComponent,
});
